/**
 * ChallengePageFormFirst.js
 *
 * The C arm of the challenge page ABC test (Rob, 2026-09-23).
 *
 * WHY THIS EXISTS:
 * - Rob on the control page: "put the form above the fold for mobile. i think
 *   that one change would make a big difference" and, pointing at the catalog
 *   paragraph in the hero, "i think this is way too much text".
 * - Most visitors arrive on a phone. On control the form starts below about
 *   seventy words of catalog text, Rob's bio and a countdown. Here a short
 *   promise, the live date and the whole form fit on the first phone screen.
 * - The success measure is lead conversion rate (seats saved per visitor) and
 *   therefore cost per lead.
 *
 * IMPORTANT BEHAVIORS:
 * - Presentation only. Registration, the held press while the catalog loads,
 *   phone validation, SMS consent and the Lead event all stay in
 *   ChallengePage, so this arm can never drift from A on those rules.
 * - The kickoff date comes from the catalog. Nothing here names a weekday or
 *   time, because the challenge moves between days.
 * - One goal on the page. Every button returns to the one form, and a small
 *   bar keeps that button in reach on a phone once the form scrolls away.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ROB_LINKEDIN_URL } from '../../components/course/courseContent';
import { useChallengeFormFirstCopy } from './challengeFormFirstCopy';
import './ChallengePageFormFirst.css';

const FORM_FIRST_CTA = 'Save My Free Seat';
const FORM_FIRST_SUBHEADING =
  'Free live class. Set up an AI that researches the market and trades for you with real money.';

const FORM_FIRST_BENEFITS = [
  {
    title: 'Your own AI trading setup',
    body: 'Rob builds it with you, live on Zoom. You keep it.',
  },
  {
    title: 'A real trade you understand',
    body: 'Pick a trade in any market, plan it, and place it with real money.',
  },
  {
    title: 'Help all week',
    body: 'Short tips by email. Reply with a question and a real person answers.',
  },
];

const FORM_FIRST_FAQS = [
  { q: 'Is it really free?', a: 'Yes. No credit card.' },
  {
    q: 'Do I need a broker?',
    a: 'Only when you are ready to trade. Rob shows you how to connect one.',
  },
  { q: 'What if I miss it?', a: 'It runs every week. Save a seat for the next one.' },
  {
    q: 'Will you sell me something?',
    a: 'At the end, Rob briefly explains the paid course. You never have to buy.',
  },
];

/**
 * Scrolls the one form into view and puts the cursor in its first field.
 * Explicit scrolling, because a plain anchor jump can land the form under the
 * fixed site header.
 */
const jumpToForm = (event) => {
  event.preventDefault();
  const target = document.getElementById('challenge-register');
  if (!target) return;
  target.scrollIntoView({ block: 'center' });
  target.querySelector('input:not([disabled])')?.focus({ preventScroll: true });
};

/**
 * True once the form has scrolled off screen. Browsers without
 * IntersectionObserver never show the bar, which only costs a convenience.
 */
const useFormOffScreen = (enabled) => {
  const [offScreen, setOffScreen] = useState(false);

  useEffect(() => {
    if (!enabled || !('IntersectionObserver' in window)) {
      setOffScreen(false);
      return undefined;
    }
    const target = document.getElementById('challenge-register');
    if (!target) return undefined;
    const observer = new window.IntersectionObserver(
      ([entry]) => setOffScreen(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [enabled]);

  return offScreen;
};

const ChallengePageFormFirst = ({
  challengeName,
  registrationForm,
  registrationClosed,
  registrationStatus,
  loadError,
  heldRegistration,
  onRetrySchedule,
  kickoffLabel,
  isLoading,
  contactUrl,
  coursePath,
}) => {
  const adMatchCopy = useChallengeFormFirstCopy();
  const formOffScreen = useFormOffScreen(!registrationClosed);
  const showStickyCta = formOffScreen && !registrationClosed;

  return (
    <div className="chff" data-testid="challenge-form-first">
      <header className="chff-hero" data-testid="challenge-form-first-hero">
        <div className="chff-intro" data-testid="challenge-form-first-intro">
          {/* Rob, 2026-09-23: on B the title is the challenge's name from the
              catalog, same as A. Never rename it. Rob, 2026-10-02: arm C
              (form_first_ad_match) swaps only this heading and subheading for
              the top Proven ad's words, provided by the experiment wrapper. */}
          <h1 className="chff-title">{adMatchCopy ? adMatchCopy.heading : challengeName}</h1>
          <p className="chff-sub" data-testid="challenge-form-first-subheading">
            {adMatchCopy ? adMatchCopy.subheading : FORM_FIRST_SUBHEADING}
          </p>
          <p className="chff-when" data-testid="challenge-when">
            <span className="chff-when__dot" aria-hidden="true" />
            Live on Zoom · {kickoffLabel || (isLoading ? 'Loading date' : 'Date coming soon')}
          </p>
        </div>

        <div className="chff-form">
          {registrationClosed ? (
            <div
              className="chp-registration-status chff-status"
              id="challenge-register"
              role="status"
            >
              <h2>{registrationStatus.title}</h2>
              <p>{registrationStatus.body}</p>
              {loadError && heldRegistration ? (
                <p data-testid="challenge-held-registration">
                  Your details are still filled in. Press Try Again to finish saving your seat.
                </p>
              ) : null}
              <div className="chp-registration-status__actions">
                {loadError ? (
                  <button type="button" className="cp-btn-primary" onClick={onRetrySchedule}>
                    Try Again
                  </button>
                ) : null}
                <a className={loadError ? undefined : 'cp-btn-primary'} href={contactUrl}>
                  Ask About The Next Live Week
                </a>
                <Link to={coursePath}>See The Five-Week Course</Link>
              </div>
            </div>
          ) : (
            <div id="challenge-register">{registrationForm}</div>
          )}
        </div>

        <a
          className="chff-host"
          data-testid="challenge-form-first-host"
          href={ROB_LINKEDIN_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src="/images/rob.jpg" alt="" width="44" height="44" decoding="async" />
          <span>
            <strong>Hosted by Rob Grzesik</strong>
            <span>Built 1,000+ trading systems. Taught 14,000+ students.</span>
          </span>
        </a>
      </header>

      <section
        className="chff-details"
        data-testid="challenge-form-first-details"
        aria-labelledby="chff-details-heading"
      >
        <h2 id="chff-details-heading">What you get</h2>
        <ul>
          {FORM_FIRST_BENEFITS.map((item) => (
            <li key={item.title}>
              <strong>{item.title}</strong>
              <span>{item.body}</span>
            </li>
          ))}
        </ul>
      </section>

      <section
        className="chff-faq"
        data-testid="challenge-faq"
        id="challenge-questions"
        aria-labelledby="chff-faq-heading"
      >
        <h2 id="chff-faq-heading">Questions</h2>
        <dl>
          {FORM_FIRST_FAQS.map((item) => (
            <div key={item.q}>
              <dt>{item.q}</dt>
              <dd>{item.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      {registrationClosed ? null : (
        <div className="chff-bottom">
          <a
            className="cp-btn-primary"
            href="#challenge-register"
            onClick={jumpToForm}
            data-testid="challenge-form-first-bottom-cta"
          >
            {FORM_FIRST_CTA}
          </a>
        </div>
      )}

      {showStickyCta ? (
        <div className="chff-sticky">
          <a
            className="cp-btn-primary"
            href="#challenge-register"
            onClick={jumpToForm}
            data-testid="challenge-form-first-sticky-cta"
          >
            {FORM_FIRST_CTA}
          </a>
        </div>
      ) : null}
    </div>
  );
};

export { FORM_FIRST_CTA };
export default ChallengePageFormFirst;
